import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, ProgressBar } from "react-bootstrap";
import { useLocalStorage } from "../../useLocalStorage";
import config from "./config.json";

const Step4 = ({ prevStep, nextStep }) => {
  const currentStep = 4;

  // On garde le même storage key si tu veux récupérer les anciennes données
  const [dataLocal, setDataLocal] = useLocalStorage("audit_energetique_step4", {
    address: "",
  });

  const [query, setQuery] = useState(dataLocal.address || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [errorSuggestions, setErrorSuggestions] = useState(null);

  // Appel à l'API Adresse (autocomplétion)
  useEffect(() => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setErrorSuggestions(null);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        setIsLoadingSuggestions(true);
        setErrorSuggestions(null);

        const res = await fetch(
            `/wp-content/plugins/tunneldevente-energia/api-adresse-proxy.php?q=${encodeURIComponent(
            query
          )}&limit=5`,
          { signal: controller.signal }
        );

        if (!res.ok) {
          throw new Error("Erreur lors de l'appel à l'API Adresse");
        }

        const data = await res.json();
        setSuggestions(data.features || []);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error(err);
          setErrorSuggestions("Impossible de récupérer les suggestions d'adresse.");
        }
      } finally {
        setIsLoadingSuggestions(false);
      }
    }, 300); // petit debounce de 300ms

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [query]);

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setDataLocal((prev) => ({ ...prev, address: value }));
  };

  const handleSelectSuggestion = (feature) => {
    const label = feature.properties?.label || "";
    setQuery(label);
    setDataLocal((prev) => ({
      ...prev,
      address: label,
      // si tu veux stocker des infos en plus :
      postcode: feature.properties?.postcode || "",
      city: feature.properties?.city || "",
      longitude: feature.geometry?.coordinates?.[0],
      latitude: feature.geometry?.coordinates?.[1],
    }));
    setSuggestions([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!dataLocal.address) return;
    nextStep();
  };

  return (
    <Container>
      <ProgressBar now={(currentStep / config.totalSteps) * 100} />

      <Form onSubmit={handleSubmit}>
        {/* Titre */}
        <Row>
          <Col>
            <h2>{config.step4.step}</h2>
            <h3>{config.step4.title}</h3>
          </Col>
        </Row>

        {/* Champ adresse avec autocomplétion */}
        <Row className="mb-3 justify-content-center">
          <Col lg={6} className="position-relative">
            <Form.Label>{config.step4.label}</Form.Label>
            <Form.Control
              type="text"
              name="address"
              value={query}
              onChange={handleAddressChange}
              placeholder="Ex : 10 Rue de la Paix, 75002 Paris"
              autoComplete="off"
              required
            />

            {isLoadingSuggestions && (
              <div className="small text-muted mt-1">Recherche en cours...</div>
            )}

            {errorSuggestions && (
              <div className="small text-danger mt-1">{errorSuggestions}</div>
            )}

            {suggestions.length > 0 && (
              <div className="adresse-suggestions border rounded bg-white shadow-sm mt-1 text-start">
                {suggestions.map((feature) => {
                  const label = feature.properties?.label;
                  const key =
                    feature.properties?.id ||
                    `${feature.geometry?.coordinates?.join(",")}-${label}`;

                  return (
                    <div
                      key={key}
                      className="p-2 suggestion-item"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSelectSuggestion(feature)}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            )}
          </Col>
        </Row>

        {/* Boutons */}
        <Row className="mt-3">
          <Col className="d-flex justify-content-between">
            <Button variant="secondary" onClick={prevStep}>
              Précédent
            </Button>
            <Button type="submit" variant="primary">
              Suivant
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default Step4;
